/**
 * Pure decision logic for `useSessionStream` (docs/games/word-delve/
 * GAME_PLAN.md §15.1), kept unit-testable without a DOM/`EventSource`
 * harness — matching this codebase's convention of extracting presentation
 * decisions into plain functions (`lib/connection-state.ts`,
 * `lib/session-outcome.ts`).
 */

/** After this many consecutive `EventSource` `onerror` events, the hook stops subscribing entirely for that session (docs/games/word-delve/GAME_PLAN.md §15.1: "two-strike degradation"). */
export const SESSION_STREAM_ERROR_THRESHOLD = 2;

/** The next consecutive-error count after another `onerror` event. */
export function nextConsecutiveErrorCount(current: number): number {
  return current + 1;
}

/** Whether the stream should be considered degraded (and therefore stopped) at this consecutive-error count. */
export function isSessionStreamDegraded(consecutiveErrors: number): boolean {
  return consecutiveErrors >= SESSION_STREAM_ERROR_THRESHOLD;
}

/**
 * Whether a `revision` frame should trigger a query invalidation: only when
 * its version is strictly greater than whatever version is currently
 * cached (or when nothing is cached yet). A frame at or below the cached
 * version is a harmless no-op — replaying an old frame, or one that raced
 * an already-newer poll result, must never cause a redundant refetch.
 */
export function shouldInvalidateOnRevision(
  cachedVersion: number | undefined,
  frameVersion: number,
): boolean {
  return cachedVersion === undefined || frameVersion > cachedVersion;
}
